/**
 * The shipped model is a sparse table of small integers: for every hashed
 * feature, which classes it pushes and by how much (int8, one scale per class).
 * This file turns trained float weights into that table, the table into bytes
 * and back. The bytes are what `model.blob.ts` carries as base64.
 */

export type DenseWeights = {
  dim: number;
  /** Intent columns first, then domain columns. */
  columns: number;
  nIntent: number;
  /** Row-major: the weights of feature `f` start at `f * columns`. */
  weights: Float32Array;
  bias: Float32Array;
};

export type Model = {
  dim: number;
  columns: number;
  nIntent: number;
  bias: Float32Array;
  /** One factor per column: a stored integer times its scale is the weight. */
  scale: Float32Array;
  /** Compressed rows: the entries of feature `f` are `rowStart[f]` to `rowStart[f + 1]`. */
  rowStart: Uint32Array;
  col: Uint8Array;
  q: Int8Array;
};

const MAGIC = [0x44, 0x52, 0x54, 0x31]; // "DRT1"
const HEADER_BYTES = 28;
const Q_MAX = 127;
/** Columns are stored as one byte. */
const MAX_COLUMNS = 256;

type QuantizeOptions = {
  /** Weights smaller than this (in weight units) are dropped to save space. */
  prune?: number;
};

export function quantize({ dim, columns, nIntent, weights, bias }: DenseWeights, { prune = 0 }: QuantizeOptions = {}): Model {
  const scale = Float32Array.from({ length: columns }, (_, column) => {
    let widest = 0;
    for (let row = 0; row < dim; row += 1) widest = Math.max(widest, Math.abs(weights[row * columns + column] ?? 0));
    return widest > 0 ? widest / Q_MAX : 1;
  });
  const rowStart = new Uint32Array(dim + 1);
  const cols: number[] = [];
  const quants: number[] = [];
  for (let row = 0; row < dim; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const weight = weights[row * columns + column] ?? 0;
      if (Math.abs(weight) < prune) continue;
      const q = Math.max(-Q_MAX, Math.min(Q_MAX, Math.round(weight / (scale[column] ?? 1))));
      if (q === 0) continue;
      cols.push(column);
      quants.push(q);
    }
    rowStart[row + 1] = cols.length;
  }
  return { dim, columns, nIntent, bias: Float32Array.from(bias), scale, rowStart, col: Uint8Array.from(cols), q: Int8Array.from(quants) };
}

function writeVarint(out: number[], value: number): void {
  let rest = value;
  while (rest >= 0x80) {
    out.push((rest & 0x7f) | 0x80);
    rest = Math.floor(rest / 128);
  }
  out.push(rest);
}

export function encodeModel(model: Model, classKey: number, featureKey: number): Uint8Array {
  const body: number[] = [];
  let rows = 0;
  let previous = -1;
  for (let row = 0; row < model.dim; row += 1) {
    const from = model.rowStart[row] ?? 0;
    const to = model.rowStart[row + 1] ?? from;
    if (to === from) continue;
    writeVarint(body, row - previous);
    writeVarint(body, to - from);
    for (let at = from; at < to; at += 1) body.push(model.col[at] ?? 0, (model.q[at] ?? 0) & 0xff);
    previous = row;
    rows += 1;
  }
  const floats = model.columns * 4 * 2;
  const bytes = new Uint8Array(HEADER_BYTES + floats + body.length);
  const view = new DataView(bytes.buffer);
  bytes.set(MAGIC, 0);
  view.setUint32(4, classKey >>> 0, true);
  view.setUint32(8, model.dim, true);
  view.setUint16(12, model.columns, true);
  view.setUint16(14, model.nIntent, true);
  view.setUint32(16, rows, true);
  view.setUint32(20, model.col.length, true);
  view.setUint32(24, featureKey >>> 0, true);
  model.bias.forEach((value, at) => view.setFloat32(HEADER_BYTES + at * 4, value, true));
  model.scale.forEach((value, at) => view.setFloat32(HEADER_BYTES + (model.columns + at) * 4, value, true));
  bytes.set(body, HEADER_BYTES + floats);
  return bytes;
}

export function decodeModel(bytes: Uint8Array, classKey: number, featureKey: number): Model {
  if (bytes.length < HEADER_BYTES || MAGIC.some((byte, at) => bytes[at] !== byte)) throw new Error('These bytes are not a router model.');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint32(4, true) !== classKey >>> 0) throw new Error('The router model was trained for other classes than the ones in the code.');
  if (view.getUint32(24, true) !== featureKey >>> 0) throw new Error('The router model was trained with other features than the code reads: train it again.');
  const dim = view.getUint32(8, true);
  const columns = view.getUint16(12, true);
  const nIntent = view.getUint16(14, true);
  const rows = view.getUint32(16, true);
  const nnz = view.getUint32(20, true);
  const corrupt = () => new Error('The router model is truncated or corrupt.');
  if (columns === 0 || columns > MAX_COLUMNS || nIntent > columns || rows > dim || nnz > rows * columns) throw corrupt();
  if (bytes.length < HEADER_BYTES + columns * 8) throw corrupt();
  const bias = Float32Array.from({ length: columns }, (_, at) => view.getFloat32(HEADER_BYTES + at * 4, true));
  const scale = Float32Array.from({ length: columns }, (_, at) => view.getFloat32(HEADER_BYTES + (columns + at) * 4, true));
  const rowStart = new Uint32Array(dim + 1);
  const col = new Uint8Array(nnz);
  const q = new Int8Array(nnz);
  let cursor = HEADER_BYTES + columns * 8;
  const readVarint = (): number => {
    let value = 0;
    let shift = 1;
    for (;;) {
      if (cursor >= bytes.length) throw corrupt();
      const byte = bytes[cursor] ?? 0;
      cursor += 1;
      value += (byte & 0x7f) * shift;
      if (byte < 0x80) return value;
      shift *= 128;
    }
  };
  let row = -1;
  let filled = 0;
  const counts = new Uint32Array(dim);
  for (let seen = 0; seen < rows; seen += 1) {
    row += readVarint();
    const count = readVarint();
    if (row >= dim || filled + count > nnz || cursor + count * 2 > bytes.length) throw corrupt();
    counts[row] = count;
    for (let k = 0; k < count; k += 1) {
      col[filled] = bytes[cursor] ?? 0;
      q[filled] = ((bytes[cursor + 1] ?? 0) << 24) >> 24;
      cursor += 2;
      filled += 1;
    }
  }
  if (filled !== nnz || cursor !== bytes.length) throw corrupt();
  for (let at = 0; at < dim; at += 1) rowStart[at + 1] = (rowStart[at] ?? 0) + (counts[at] ?? 0);
  return { dim, columns, nIntent, bias, scale, rowStart, col, q };
}

export function toBase64(bytes: Uint8Array): string {
  const chunk = 0x8000;
  let binary = '';
  for (let at = 0; at < bytes.length; at += chunk) binary += String.fromCharCode(...bytes.subarray(at, at + chunk));
  return btoa(binary);
}

export function fromBase64(text: string): Uint8Array {
  const binary = atob(text);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}
